/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { FaultyIntent } from 'src/luis/projects/analytics/types/FaultyIntent';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { IntentTrainingResult } from 'src/luis/projects/analytics/types/IntentTrainingResult';
import { LuisId } from 'src/luis/types/Resource';

/**
 * Determines which recommendations decisions the given intent needs.
 *
 * @param selectedIntent The intent to be checked.
 * @param onLoadGiantIntent A callback to load some intent trining result if needed from the backend.
 */
export const useHighlightedRecommendationsDecider = (
    selectedIntent: IntentMetadata,
    onLoadGiantIntent: (giantIntentId: LuisId) => void
) => {
    const { rootStore } = useGlobalState();
    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;

    const giantIntent = getMostConflictedIntent(selectedIntent, analyticsStore.intentsTrainingResult);

    React.useEffect(() => {
        if (giantIntent && !analyticsStore.intentsTrainingResult.has(giantIntent.id)) {
            onLoadGiantIntent(giantIntent.id);
        }
    }, [giantIntent && !analyticsStore.intentsTrainingResult.has(giantIntent.id)]);

    return {
        needMerging: selectedIntent && needMerging(selectedIntent, analyticsStore),
        needPatterns: selectedIntent && needPatterns(selectedIntent, analyticsStore),
        needSplitting: selectedIntent && needSplitting(selectedIntent, analyticsStore, giantIntent),
        isDataImbalanced:
            selectedIntent && isDataImbalanced(selectedIntent, [...analyticsStore.needyIntents, ...analyticsStore.greedyIntents])
    };
};

/**
 * Decides if the given intent one of the data imbalanced intents.
 *
 * @param intent the intent to be checked.
 */
const isDataImbalanced = (intent: IntentMetadata, dataImbalancedIntents: IntentMetadata[]): boolean =>
    dataImbalancedIntents.reduce((acc, val) => acc || val.intentId === intent.intentId, false);

/**
 * Decides if the given intent needs adding pattern to differentiate between similar words.
 * We use the following heuristics:
 *     conflictedIntentsWithSelectedIntentCount
 *     ----------------------------------------   > threshold * 0.2
 *                 intentsCount - 1
 *
 * where:
 *                    --> min(1, 1 / log(intentsCount - 1))    if intentsCount > 1
 *     threshold = --|
 *                    --> 0                                    otherwise
 *
 * @param intent the intent to be checked.
 * @param metadata The analytics metadata of the current app.
 */
const needPatterns = (
    intent: IntentMetadata,
    metadata: { intentsCount: number; intentsTrainingResult: Map<LuisId, IntentTrainingResult> }
): boolean => {
    if (!metadata.intentsTrainingResult.has(intent.intentId)) {
        return false;
    }

    const incorrectIntents = metadata.intentsTrainingResult.get(intent.intentId).incorrectWith;
    const unclearIntents = metadata.intentsTrainingResult.get(intent.intentId).unclearWith;

    const intents = new Map<LuisId, FaultyIntent>();
    incorrectIntents.forEach(incorrectIntent => intents.set(incorrectIntent.id, incorrectIntent));
    unclearIntents.forEach(unclearIntent => {
        if (intents.has(unclearIntent.id)) {
            intents.set(unclearIntent.id, {
                id: unclearIntent.id,
                name: unclearIntent.name,
                utterancesCount: unclearIntent.utterancesCount + intents.get(unclearIntent.id).utterancesCount
            });
        } else {
            intents.set(unclearIntent.id, unclearIntent);
        }
    });
    const threshold = metadata.intentsCount > 3 ? Math.min(1, 1 / Math.log10(metadata.intentsCount - 1)) : 1;

    return intents.size / (metadata.intentsCount - 1) > threshold * 0.2;
};

/**
 * Decides if the given intent needs to be merged.
 * Note that: for the selected intent (A) with any other conflicted intent (B)
 * we use the following heuristics:
 *            conflictedUtterancesFromAtoBCount
 *    sigma   ---------------------------------   > 0.4 for all B where B != A
 *                 totalUtterancesCountInA
 *
 * @param intent the intent to be checked.
 * @param metadata The analytics metadata of the current app.
 */
const needMerging = (intent: IntentMetadata, metadata: { intentsTrainingResult: Map<LuisId, IntentTrainingResult> }): boolean => {
    if (!metadata.intentsTrainingResult.has(intent.intentId)) {
        return false;
    }

    const incorrectIntents = metadata.intentsTrainingResult.get(intent.intentId).incorrectWith;

    return incorrectIntents
        .filter(incorrectIntent => incorrectIntent.id !== intent.intentId)
        .reduce((acc, value) => {
            const ratio = value.utterancesCount / intent.numUtterances;

            return acc || ratio > 0.4;
        }, false);
};

/**
 * Decides if the given intent needs to be split.
 * Note that: for the selected intent (A) with any other conflicted intent (B)
 * we use the following heuristics:
 *       --          conflictedUtterancesFromAtoBCount                                 --
 * not -|    sigma   ---------------------------------   > 0.4 for all B where B != A    |-
 *       --              totalUtterancesCountInA                                       --
 *
 *       --          conflictedUtterancesFromAtoBCount * conflictedUtterancesFromBtoACount                                --
 * and -|    sigma   ---------------------------------------------------------------------  > 0.05 for all B where B != A   |-
 *       --                   totalUtterancesCountInA * totalUtterancesCountInB                                           --
 *
 *
 * @param intent the intent to be checked.
 * @param metadata The analytics metadata of the current app.
 * @param giantIntent The most conflicted intent with the given intent.
 */
const needSplitting = (
    intent: IntentMetadata,
    metadata: { utterancesPerIntent: Map<LuisId, IntentMetadata>; intentsTrainingResult: Map<LuisId, IntentTrainingResult> },
    giantIntent: FaultyIntent
): boolean => {
    if (!giantIntent) {
        return false;
    }

    const giantIntentIntents = getEngagedIntents(giantIntent.id, metadata.intentsTrainingResult);

    const _selectedIntent = giantIntentIntents.get(intent.intentId);

    return (
        giantIntent &&
        !needMerging(intent, metadata) &&
        _selectedIntent &&
        (giantIntent.utterancesCount * _selectedIntent.utterancesCount) /
            (intent.numUtterances * metadata.utterancesPerIntent.get(giantIntent.name).numUtterances) >
            0.05
    );
};

/**
 * Gets the intents which are conflicted predicted with the given intent
 * associated with the number of utterances that have conflict.
 *
 * @param intentId The selected intent to get the engaged intents for.
 * @param metadata The analytics metadata of the current app.
 * @returns  map where the key is the intent id and the value is
 * the associated number of conflicted utterances.
 */
const getEngagedIntents = (intentId: LuisId, intentsTrainingResult: Map<LuisId, IntentTrainingResult>): Map<LuisId, FaultyIntent> => {
    const intentTrainingResult = intentsTrainingResult.get(intentId);
    const incorrectIntents: FaultyIntent[] = intentTrainingResult ? intentTrainingResult.incorrectWith : [];
    const unclearIntents: FaultyIntent[] = intentTrainingResult ? intentTrainingResult.unclearWith : [];

    const intents = new Map<LuisId, FaultyIntent>();

    incorrectIntents.forEach(intent => intents.set(intent.id, intent));
    unclearIntents.forEach(intent => {
        if (intents.has(intent.id)) {
            intents.set(intent.id, {
                id: intent.id,
                name: intent.name,
                utterancesCount: intent.utterancesCount + intents.get(intent.id).utterancesCount
            });
        } else {
            intents.set(intent.id, intent);
        }
    });

    return intents;
};

/**
 * Gets the most conflicted intent with the given intent.
 *
 * @param intent The intent to get the most conflicted intent for.
 * @param metadata The analytics metadata of the current app.
 */
export const getMostConflictedIntent = (intent: IntentMetadata, intentsTrainingResult: Map<LuisId, IntentTrainingResult>) => {
    if (!intent || !intentsTrainingResult.has(intent.intentId)) {
        return null;
    }

    const intents = getEngagedIntents(intent.intentId, intentsTrainingResult);

    const sortedIntentsArr = Array.from(intents.entries()).sort((a, b) => (a[1].utterancesCount < b[1].utterancesCount ? 1 : -1));

    if (!sortedIntentsArr.length) {
        return null;
    }

    return sortedIntentsArr[0][1];
};
